import React, { useState } from 'react';
import {
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  LinearProgress,
  Paper,
  TextField
} from '@material-ui/core';
import { show } from 'redux-modal';
import lodash from 'lodash';
import { useDispatch } from 'react-redux';
import View from '../../components/view/View';
import Button from '../../components/button/Button';
import {
  getUser,
  updateUserInformation
} from '../../redux/actions/user-actions';
import {
  MODAL_CHANGE_PASSWORD,
  MODAL_INVITE_ADMIN,
  MODAL_VIEW_ADMINS
} from '../../constants/modals';
import { userTypes } from '../../constants/users';
import features from '../../utils/features';
import { refreshToken } from '../../redux/login/login-actions';
import { useAppSelector } from '../../redux/hooks';
import { fetchApi } from '../../redux/actions/fetch-actions';
import { APIMethods } from '../../types/fetch/fetch-types';
import { AxiosError, AxiosResponse } from 'axios';
import { push } from 'react-router-redux';
import { commonRoutes } from '../../constants/routes';
import moment from 'moment';
import { IBillingDetail } from '../../features/billing/interfaces/IBillingDetail';

const Account: React.FC = () => {
  const dispatch = useDispatch();
  const userData = useAppSelector(state => state.userData);
  const login = useAppSelector(state => state.login);
  const isPremiumUser = userData.premium && userData.premium.isPremium;
  const expiryDate = moment(userData.premium.expiryDate).format(
    'MMMM DD, YYYY'
  );
  const original = {
    firstName: userData.firstName,
    lastName: userData.lastName,
    email: userData.email
  };
  React.useEffect(() => {
    if (isPremiumUser) {
      dispatch(refreshToken());
      dispatch(getUser(login.token, false, undefined, false));
    }
  }, []);
  const [account, setAccount] = useState(original);
  const [administrators, setAdministrators] = useState() as any;
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [expiry, setExpiry] = React.useState<IBillingDetail>();

  const GoToBillingPage = () => {
    dispatch(push(commonRoutes.billingPage));
  };

  React.useEffect(() => {
    dispatch(
      fetchApi({
        url: 'stripe/billing',
        method: APIMethods.GET
      })
    )
      .then((result: AxiosResponse) => {
        const { billing } = result.data;
        const { activePlan, invoices } = billing;
        setExpiry({
          invoices,
          activePlan
        });
      })
      .catch((err: AxiosError) => {
        console.error(err);
      });
  }, []);

  function handleUpdateInformation(): any {
    if (password.length > 0) {
      dispatch(
        show('spinner', {
          text: 'Updating user information'
        })
      );
      const newAccountInfo: any = { ...account };
      if (password.length > 0) {
        newAccountInfo.password = password;
      }
      dispatch(
        updateUserInformation({
          ...userData,
          ...newAccountInfo
        })
      );
      return null;
    }
    if (lodash.isEqual(original, account)) {
      return dispatch(
        show('snackbar', {
          variant: 'warning',
          message: 'No changes detected'
        })
      );
    }
    const newAccountInfo: any = { ...account };
    if (password.length > 0) {
      newAccountInfo.password = password;
    }
    dispatch(
      updateUserInformation({
        ...userData,
        ...newAccountInfo
      })
    );
    return null;
  }

  function handleChange(evt: any) {
    setAccount({
      ...account,
      [evt.target.name]: evt.target.value
    });
  }

  function handleInviteAdmin() {
    dispatch(
      show('modal', {
        type: MODAL_INVITE_ADMIN,
        open: true
      })
    );
  }

  function handleViewAdmins(adminType: string) {
    let admins = administrators.schoolAdmins;
    if (adminType === 'district') {
      admins = administrators.districtAdmins;
    }
    dispatch(
      show('modal', {
        type: MODAL_VIEW_ADMINS,
        open: true,
        admins
      })
    );
  }

  return (
    <View title="Account Information">
      <Grid
        container
        justify="center"
        style={{
          height: '80vh',
          overflow: 'hidden'
        }}
      >
        <Grid item xs={12}>
          <Paper
            style={{
              padding: '30px'
            }}
          >
            <Grid
              container
              justify="center"
              alignItems="center"
              spacing={4}
              style={{
                textAlign: 'center'
              }}
            >
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    label="First Name"
                    value={account.firstName}
                    name="firstName"
                    onChange={handleChange}
                    fullWidth
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    label="Last Name"
                    value={account.lastName}
                    name="lastName"
                    onChange={handleChange}
                    fullWidth
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    label="Email"
                    value={account.email}
                    name="email"
                    disabled
                    fullWidth
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    label="Account Type"
                    value={userData.userType}
                    disabled
                    fullWidth
                  />
                </FormControl>
              </Grid>
              {userData.userType === userTypes.teacher &&
                features.teacher.teacherInviteAdmin && (
                  <>
                    <Grid
                      item
                      xs={12}
                      style={{ textAlign: 'left' }}
                      alignItems={'flex-start'}
                    >
                      <p>School Administrator Contact(s)</p>
                      {!administrators ||
                        (administrators.schoolAdmins.length === 0 && (
                          <>
                            <p>
                              No administrator connected -{' '}
                              <span
                                style={{ cursor: 'pointer', color: 'blue' }}
                                onClick={handleInviteAdmin}
                              >
                                Share Dreamscape with your Administrator
                              </span>
                            </p>
                          </>
                        ))}
                      {administrators &&
                        administrators.schoolAdmins.length > 0 && (
                          <span
                            onClick={() => handleViewAdmins('school')}
                            style={{ cursor: 'pointer', color: 'blue' }}
                          >
                            View Admins
                          </span>
                        )}
                      {loading && <LinearProgress />}
                      <Divider
                        style={{ marginTop: '20px' }}
                        orientation="horizontal"
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      style={{ textAlign: 'left' }}
                      alignItems={'flex-start'}
                    >
                      <p>District Administrator Contact(s)</p>
                      {!administrators ||
                        (administrators.districtAdmins.length === 0 && (
                          <>
                            <p>
                              No administrator connected -{' '}
                              <span
                                style={{ cursor: 'pointer', color: 'blue' }}
                                onClick={handleInviteAdmin}
                              >
                                Share Dreamscape with your Administrator
                              </span>
                            </p>
                          </>
                        ))}
                      {administrators &&
                        administrators.districtAdmins.length > 0 && (
                          <span
                            onClick={() => handleViewAdmins('district')}
                            style={{ cursor: 'pointer', color: 'blue' }}
                          >
                            View Admins
                          </span>
                        )}
                      {loading && <LinearProgress />}
                      <Divider
                        style={{ marginTop: '20px' }}
                        orientation="horizontal"
                      />
                    </Grid>
                  </>
                )}
              {userData.userType === userTypes.schoolAdmin && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      label="Member of"
                      value={`${userData.schools[0].name} (${userData.schools[0].schoolCode})`}
                      disabled
                      fullWidth
                    />
                  </FormControl>
                </Grid>
              )}
              {isPremiumUser && (
                <>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <TextField
                        label="Your Plan"
                        value={userData.premium.type}
                        disabled
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                style={{ marginBottom: '19px' }}
                                primary={true}
                                onClick={GoToBillingPage}
                              >
                                Manage subscription
                              </Button>
                            </InputAdornment>
                          )
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <TextField
                        label="Memberships Assigned"
                        value={`${userData.premium.membershipsAllocated} / ${userData.premium.totalMemberships}`}
                        disabled
                        fullWidth
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <TextField
                        label={
                          expiry?.activePlan.cancelAtPeriodEnd
                            ? 'Plan Expiry Date'
                            : 'Plan Renewal Date'
                        }
                        value={expiryDate}
                        disabled
                        fullWidth
                      />
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>

            <Grid
              container
              item
              xs={12}
              justify="flex-end"
              style={{ marginTop: 40 }}
            >
              <Button
                style={{ marginRight: 10 }}
                onClick={() => {
                  dispatch(
                    show('modal', {
                      type: MODAL_CHANGE_PASSWORD
                    })
                  );
                }}
              >
                Change Password
              </Button>
              <Button onClick={handleUpdateInformation} primary>
                Save Account Information
              </Button>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </View>
  );
};

export default React.memo(Account);
